import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Form, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { UserService } from '../../user/UserService';
import { IUser } from '../../../model/user/IUser';
import { CampaignsService } from '../../campaigns/CampaignsService';
import { Campaign } from '../../../model/campaign/Campaign';
import { Company } from '../../../model/company/Company';
import { CompanyService } from '../../company/CompanyService';
import { getStatusLabel } from '../../../utils/Functions';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import MultiSelectDropdown from '../../../shared/components/MultiSelectDropdown';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import { FaTimes } from 'react-icons/fa';
import { ErrorToast } from '../../../utils/toasters';

interface IOfferFilter {
    handleState: (...arg: any) => void;
    setState: (...arg: any) => void;
    offersFilter: any;
    onExport?: () => void;
    isExporting?: boolean;
    isDisabled?: boolean;
}

export function OfferFilter({ handleState, setState, offersFilter, onExport, isExporting, isDisabled }: IOfferFilter) {
    const [users, setUsers] = useState<IUser[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [userFilter, setUserFilter] = useState('');
    const [campaignFilter, setCampaignFilter] = useState('');
    const [companies, setCompanies] = useState<Company[]>([]);
    const [companyFilter, setCompanyFilter] = useState('');
    const [inputTypes, setInputTypes] = useState({
        date: 'text',
        acceptedDateFrom: 'text',
        acceptedDateTo: 'text',
    });
    const selectedStatuses: string[] = offersFilter?.multipleStatuses
        ? String(offersFilter.multipleStatuses).split(',')
        : [];
    const showAcceptedRange = selectedStatuses.some(status => ['accepted', 'completed'].includes(status));

    const { offerStatus } = useSelector((offersFilter: any) => offersFilter.enums);

    useEffect(() => {
        UserService.getUsers({ perPage: 15, displayName: userFilter })
            .then(data => {
                setUsers(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [userFilter]);

    useEffect(() => {
        CampaignsService.getCampaigns({ perPage: 15, title: campaignFilter, isLongTerm: false })
            .then(data => {
                setCampaigns(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [campaignFilter]);

    useEffect(() => {
        CompanyService.getCompanies({ perPage: 15, displayNameOrId: companyFilter })
            .then(data => {
                setCompanies(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [companyFilter]);

    function handleChanges(evt: any) {
        const value = evt.target.value;
        setState({
            ...offersFilter,
            [evt.target.name]: value,
        });
    }

    function handleTypes(evt: any, type: string) {
        setInputTypes(prev => ({
            ...prev,
            [evt.target.name]: type,
        }));
    }

    return (
        <Form>
            <Row style={{ rowGap: '18px' }}>
                <div className="col-md-2 col-6">
                    <Form.Control
                        placeholder="Offer ID"
                        className="autocomplete-input"
                        name="id"
                        onChange={handleChanges}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <div className="d-flex bg-secondary-o-100 border rounded">
                        <Form.Control
                            placeholder="Date:"
                            className="autocomplete-input border-0 cursor-pointer"
                            type={inputTypes.date}
                            name="date"
                            value={offersFilter?.date || ''}
                            onFocus={e => handleTypes(e, 'date')}
                            onBlur={e => handleTypes(e, 'text')}
                            onChange={handleChanges}
                        />
                        {offersFilter?.date && (
                            <Button
                                variant=""
                                className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                onClick={() => {
                                    setState((prev: any) => ({ ...prev, date: undefined }));
                                }}
                            >
                                <FaTimes fontSize={14} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <MultiSelectDropdown
                        placeholder="All statuses"
                        options={offerStatus.map((status: string) => ({
                            value: status,
                            label: getStatusLabel(status) as string,
                        }))}
                        selected={selectedStatuses}
                        onChange={selected =>
                            setState((prev: any) => ({
                                ...prev,
                                multipleStatuses: selected.join(','),
                                ...(selected.some(status => ['accepted', 'completed'].includes(status))
                                    ? {}
                                    : { acceptedDateFrom: undefined, acceptedDateTo: undefined }),
                            }))
                        }
                    />
                </div>
                {showAcceptedRange && (
                    <>
                        <div className="col-md-2 col-6">
                            <div className="d-flex bg-secondary-o-100 border rounded">
                                <Form.Control
                                    placeholder="Accepted from:"
                                    className="autocomplete-input border-0 cursor-pointer"
                                    type={inputTypes.acceptedDateFrom}
                                    name="acceptedDateFrom"
                                    value={offersFilter?.acceptedDateFrom || ''}
                                    onFocus={e => handleTypes(e, 'date')}
                                    onBlur={e => handleTypes(e, 'text')}
                                    onChange={handleChanges}
                                />
                                {offersFilter?.acceptedDateFrom && (
                                    <Button
                                        variant=""
                                        className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                        onClick={() => {
                                            setState((prev: any) => ({ ...prev, acceptedDateFrom: undefined }));
                                        }}
                                    >
                                        <FaTimes fontSize={14} />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-2 col-6">
                            <div className="d-flex bg-secondary-o-100 border rounded">
                                <Form.Control
                                    placeholder="Accepted to:"
                                    className="autocomplete-input border-0 cursor-pointer"
                                    type={inputTypes.acceptedDateTo}
                                    name="acceptedDateTo"
                                    value={offersFilter?.acceptedDateTo || ''}
                                    onFocus={e => handleTypes(e, 'date')}
                                    onBlur={e => handleTypes(e, 'text')}
                                    onChange={handleChanges}
                                />
                                {offersFilter?.acceptedDateTo && (
                                    <Button
                                        variant=""
                                        className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                        onClick={() => {
                                            setState((prev: any) => ({ ...prev, acceptedDateTo: undefined }));
                                        }}
                                    >
                                        <FaTimes fontSize={14} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Company name or ID"
                        onSelect={setState}
                        setFilter={setCompanyFilter}
                        keyState={'companyId'}
                        keyItemSelect={'id'}
                        value={companyFilter}
                        items={companies}
                        hasClearBtn
                        renderItems={['id', 'displayName']}
                        clearFunc={() => {
                            setState((prev: any) => ({ ...prev, companyId: undefined }));
                            setCompanyFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Campaign name or ID"
                        onSelect={setState}
                        setFilter={setCampaignFilter}
                        keyState={'campaignId'}
                        keyItemSelect={'id'}
                        value={campaignFilter}
                        items={campaigns}
                        hasClearBtn
                        renderItems={['id', 'title']}
                        clearFunc={() => {
                            setState((prev: any) => ({ ...prev, campaignId: undefined }));
                            setCampaignFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="User Displayname"
                        onSelect={setState}
                        setFilter={setUserFilter}
                        keyState={'influencerId'}
                        keyItemSelect={'id'}
                        value={userFilter}
                        items={users}
                        hasClearBtn
                        renderItems={['id', 'firstName', 'lastName']}
                        clearFunc={() => {
                            setState((prev: any) => ({ ...prev, influencerId: undefined }));
                            setUserFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <SingleSelectDropdown
                        placeholder="Revenue stream"
                        options={[
                            { value: 'true', label: 'Agency' },
                            { value: 'false', label: 'Workspace' },
                        ]}
                        selected={String(offersFilter?.hasSupervisor ?? '')}
                        onChange={value => handleChanges({ target: { name: 'hasSupervisor', value } } as any)}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <SingleSelectDropdown
                        placeholder="Has PO?"
                        options={[
                            { value: 'true', label: 'With PO' },
                            { value: 'false', label: 'Without PO' },
                        ]}
                        selected={String(offersFilter?.hasPurchaseOrder ?? '')}
                        onChange={value => handleChanges({ target: { name: 'hasPurchaseOrder', value } } as any)}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <SingleSelectDropdown
                        placeholder="Include spending"
                        options={[{ value: 'true', label: 'Exclude spending' }]}
                        selected={String(offersFilter?.excludeSpending ?? '')}
                        onChange={value => handleChanges({ target: { name: 'excludeSpending', value } } as any)}
                    />
                </div>
            </Row>
            <div className="d-flex gap-2">
                <Button className="mt-4" onClick={() => handleState(offersFilter)}>
                    Search
                </Button>
                {onExport && (
                    <Button
                        className="mt-4"
                        variant="outline-primary"
                        onClick={onExport}
                        disabled={isExporting || isDisabled}
                    >
                        {isExporting ? (
                            <>
                                Generating excel...{' '}
                                <Spinner animation="border" size="sm" role="status" className="ml-1" />
                            </>
                        ) : (
                            'Export current'
                        )}
                    </Button>
                )}
            </div>
        </Form>
    );
}
